import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import finEaseHomeNavigationRoutes from "../../../utils/navigationRoutes";
import { Card, CardContent } from "@/common/components/ui/card";
import { Input } from "@/common/components/ui/input";
import { Button } from "@/common/components/ui/button";
import { Badge } from "@/projects/FinEaseHomeAndAdmin/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import { TableColumnsType } from "antd";
import { AntDTable } from "@/common/components/ui";
import {
  Search,
  Eye,
  Edit2,
  Users,
  Download,
  RefreshCw,
  // Shield,
  // IndianRupee,
  // BarChart3,
} from "lucide-react";
import UserManagementService from "@/services/FinEaseHomeAndAdmin/APIs/UserManagementService";
import { useQuery } from "@tanstack/react-query";
import type { UserDetails } from "@/utils/types/UserManagement/UserManagement";
import { notify } from "@/common/components/common/Notification";
import * as XLSX from "xlsx";

// Mock data - Replace with API calls

// const statusDistribution = [
//   { name: "Gold", value: 35, color: "#1D2C56" },
//   { name: "NPS", value: 40, color: "#66A0CD" },
//   { name: "IPO", value: 25, color: "#8AB7D9" },
// ];

const UserManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [profileFilter, setProfileFilter] = useState<
    "all" | "completed" | "incomplete"
  >("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<UserDetails[]>([]);

  useEffect(() => {
    const id = window.setTimeout(
      () => setDebouncedSearch(searchTerm.trim()),
      300
    );
    return () => window.clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, profileFilter]);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["users", page, pageSize, debouncedSearch, profileFilter],
    queryFn: () =>
      UserManagementService.listUsers(page, pageSize, {
        search: debouncedSearch || undefined,
        is_profile_completed:
          profileFilter === "completed"
            ? true
            : profileFilter === "incomplete"
              ? false
              : undefined,
      }),
    enabled: !!page && !!pageSize,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60_000,
  });

  const filteredUsers = useMemo(() => {
    const rows = (data?.data ?? []) as (UserDetails & { name?: string })[];
    const q = debouncedSearch.toLowerCase();
    return rows.filter(user => {
      if (profileFilter === "completed" && !user.is_profile_completed) {
        return false;
      }
      if (profileFilter === "incomplete" && user.is_profile_completed) {
        return false;
      }
      if (!q) {
        return true;
      }
      const name = (
        user.name ??
        user.full_name ??
        [user.first_name, user.last_name].filter(Boolean).join(" ")
      )
        .toString()
        .toLowerCase();
      const email = (user.email ?? "").toLowerCase();
      const phone = String(user.phone_number ?? "").replace(/\s/g, "");
      const qDigits = q.replace(/\D/g, "");
      const phoneDigits = phone.replace(/\D/g, "");
      return (
        name.includes(q) ||
        email.includes(q) ||
        phone.includes(q.replace(/\s/g, "")) ||
        (qDigits.length > 0 && phoneDigits.includes(qDigits))
      );
    });
  }, [data?.data, debouncedSearch, profileFilter]);

  const handleEditUser = (id: number) => {
    navigate(finEaseHomeNavigationRoutes.EDIT_USER(id));
  };

  const handleViewUser = (id: number) => {
    navigate(finEaseHomeNavigationRoutes.VIEW_USER(id));
  };

  // Column configurations for Ant Design Table
  const columns: TableColumnsType<any> = useMemo(
    () => [
      // {
      //   title: "User ID",
      //   dataIndex: "id",
      //   key: "id",
      //   width: 200,
      //   render: id => (
      //     <span className="font-bold text-muted-foreground">#{id}</span>
      //   ),
      // },
      {
        title: "User Name",
        dataIndex: "name",
        key: "userProfile",
        width: 250,
        sorter: (a, b) =>
          String(a.name ?? a.full_name ?? "").localeCompare(
            String(b.name ?? b.full_name ?? "")
          ),
        render: (_, user) => (
          <div className="flex items-center gap-3">
            {user.name && (
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 font-bold text-primary">
                {user.name?.charAt(0)}
              </div>
            )}
            {user.name ? (
              <div>
                <p className="font-bold text-secondary">{user.name}</p>
              </div>
            ) : (
              <p className="flex items-center justify-center font-bold">-</p>
            )}
          </div>
        ),
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        width: 200,
        render: (_, user) => (
          <p className="max-w-[200px] truncate font-medium text-secondary">
            {user.email}
          </p>
        ),
      },
      {
        title: "Mobile Number",
        dataIndex: "phone_number",
        key: "phone_number",
        width: 100,
        render: (_, user) => (
          <p className="font-bold text-muted-foreground">{user.phone_number}</p>
        ),
      },
      // {
      //   title: "PAN Number",
      //   dataIndex: "pan",
      //   key: "pan",
      //   width: 150,
      //   render: (_, user) => (
      //     <span className="font-medium text-secondary">
      //       {user.pan ?? "N/A"}
      //     </span>
      //   ),
      // },
      {
        title: "Status",
        dataIndex: "is_profile_completed",
        key: "is_profile_completed",
        width: 100,
        filters: [
          { text: "Completed", value: "true" },
          { text: "Incomplete", value: "false" },
        ],
        onFilter: (value, record) =>
          String(record.is_profile_completed) === String(value),
        render: (_, user) => (
          <Badge
            variant={user.is_profile_completed ? "default" : "outline"}
            className={
              user.is_profile_completed
                ? "border-[#66A0CD]/30 bg-[#66A0CD]/20 text-[#1D2C56]"
                : "border-slate-200 bg-slate-100 text-slate-500"
            }
          >
            {user.is_profile_completed ? "Completed" : "Incomplete"}
          </Badge>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        fixed: "right" as const,
        width: 100,
        render: (_, user) => (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleViewUser(user.id)}
              className="h-8 w-8 rounded-lg text-primary"
            >
              <Eye className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleEditUser(user.id)}
              className="h-8 w-8 rounded-lg text-blue-600"
            >
              <Edit2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const handleSyncData = async () => {
    try {
      const result = await refetch();
      if (result.isError) {
        notify.error(
          "Sync failed",
          (result.error as Error)?.message ?? "Could not refresh users."
        );
        return;
      }
      notify.success("Synced", "User list updated from server.");
    } catch (e: unknown) {
      notify.error(
        "Sync failed",
        e instanceof Error ? e.message : "Could not refresh users."
      );
    }
  };

  // ── Select All across pages ───────────────────────────────────────────────────
  const handleSelectAll = async (selected: boolean) => {
    if (!selected) {
      setSelectedRowKeys([]);
      setSelectedRows([]);
      return;
    }
    const total = data?.total_users ?? 0;
    // All data already in memory — just select the current page
    if (total <= filteredUsers.length) {
      const keys = filteredUsers.map((u: any) => u.id as React.Key);
      setSelectedRowKeys(keys);
      setSelectedRows(filteredUsers as UserDetails[]);
      return;
    }
    // Fetch all records in one shot
    setSelectingAll(true);
    try {
      const allData = await UserManagementService.listUsers(1, total, {
        search: debouncedSearch || undefined,
        is_profile_completed:
          profileFilter === "completed"
            ? true
            : profileFilter === "incomplete"
              ? false
              : undefined,
      });
      const allRows = (allData?.data ?? []) as UserDetails[];
      setSelectedRowKeys(allRows.map((u: any) => u.id as React.Key));
      setSelectedRows(allRows);
      notify.success("Selected", `All ${allRows.length} users selected.`);
    } catch (e: unknown) {
      notify.error(
        "Select All",
        e instanceof Error ? e.message : "Could not load all users."
      );
    } finally {
      setSelectingAll(false);
    }
  };

  // ── Export to XLSX ─────────────────────────────────────────────────────────
  const handleExportXlsx = () => {
    const rowsToExport = selectedRows.length > 0 ? selectedRows : filteredUsers;

    if (rowsToExport.length === 0) {
      notify.error("Export", "No data to export.");
      return;
    }
    setExportLoading(true);
    try {
      const sheetData = (rowsToExport as any[]).map(user => ({
        Name:
          user.name ??
          user.full_name ??
          [user.first_name, user.last_name].filter(Boolean).join(" ") ??
          "-",
        Email: user.email ?? "-",
        "Mobile Number": user.phone_number ?? "-",
        "Profile Status": user.is_profile_completed
          ? "Completed"
          : "Incomplete",
      }));
      const ws = XLSX.utils.json_to_sheet(sheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Users");
      XLSX.writeFile(wb, "users_export.xlsx");
      notify.success(
        "Export",
        `Downloaded ${rowsToExport.length} user${
          rowsToExport.length === 1 ? "" : "s"
        } as XLSX`
      );
    } catch (e: unknown) {
      notify.error(
        "Export failed",
        e instanceof Error ? e.message : "Could not generate file."
      );
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Enhanced Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
        <svg
          className="absolute right-0 top-0 h-full w-1/3 text-white/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
        </svg>
        <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
              <Users className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-white">
                User Explorer
              </h1>
              <p className="text-white/70">
                Analyze user behavior, KYC status, and investment patterns
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={() => void handleSyncData()}
              disabled={isFetching}
            >
              <RefreshCw className="h-4 w-4" /> Sync Data
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={() => void handleExportXlsx()}
              disabled={
                exportLoading || selectingAll || selectedRows.length === 0
              }
            >
              <Download className="h-4 w-4" />
              Export
              {selectedRows.length > 0 && (
                <span className="ml-1 rounded-full bg-white/20 px-1.5 py-0.5 text-xs font-semibold">
                  {selectedRows.length}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Stats & Segmentation Grid */}
      {/* <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="flex flex-col justify-center overflow-hidden rounded-xl border border-slate-100 shadow-sm transition-all hover:border-primary/20 hover:shadow-md">
          <CardContent className="flex flex-col gap-3 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Total Users
                </p>
                <p className="text-2xl font-bold text-slate-800">
                  {statusDistribution.reduce(
                    (acc, curr) => acc + curr.value,
                    0
                  )}
                </p>
              </div>
              <div className="rounded-full bg-slate-100 p-2 text-slate-600">
                <Users className="h-4 w-4" />
              </div>
            </div>
            <div className="flex w-full items-center gap-0.5 overflow-hidden rounded-full bg-slate-100">
              {statusDistribution.map(item => (
                <div
                  key={item.name}
                  className="h-1.5 first:rounded-l-full last:rounded-r-full"
                  style={{
                    width: `${(item.value / statusDistribution.reduce((acc, curr) => acc + curr.value, 0)) * 100}%`,
                    backgroundColor: item.color,
                  }}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {statusDistribution.map(stat => (
          <Card
            key={stat.name}
            className="group flex flex-col justify-center overflow-hidden rounded-xl border border-slate-100 shadow-sm transition-all hover:border-primary/20 hover:shadow-md"
          >
            <CardContent className="flex flex-col gap-3 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    {stat.name} Users
                  </p>
                  <p className="text-2xl font-bold text-slate-800">
                    {stat.value}
                  </p>
                </div>
                <div
                  className="rounded-full p-2 transition-transform group-hover:scale-110"
                  style={{
                    backgroundColor: `${stat.color}15`,
                    color: stat.color,
                  }}
                >
                  {stat.name === "Gold" && <IndianRupee className="h-4 w-4" />}
                  {stat.name === "NPS" && <Shield className="h-4 w-4" />}
                  {stat.name === "IPO" && <BarChart3 className="h-4 w-4" />}
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between text-[10px] font-medium text-slate-400">
                  <span>Progress</span>
                  <span>
                    {Math.round(
                      (stat.value /
                        statusDistribution.reduce(
                          (acc, curr) => acc + curr.value,
                          0
                        )) *
                        100
                    )}
                    %
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full transition-all duration-500 ease-out"
                    style={{
                      width: `${(stat.value / statusDistribution.reduce((acc, curr) => acc + curr.value, 0)) * 100}%`,
                      backgroundColor: stat.color,
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div> */}

      {/* Search & Filters */}
      <Card className="grid grid-cols-1 gap-4 overflow-hidden rounded-2xl border-none shadow-sm md:grid-cols-2 lg:grid-cols-3">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 lg:flex-row">
            <div className="group relative flex-1">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-primary" />
              <Input
                placeholder="Search by name, email, or mobile..."
                className="h-11 rounded-xl border-gray-100 bg-gray-50 pl-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-3">
              <Select
                value={profileFilter}
                onValueChange={v =>
                  setProfileFilter(v as "all" | "completed" | "incomplete")
                }
              >
                <SelectTrigger className="h-11 w-[160px] rounded-xl border-gray-100 bg-gray-50 font-medium">
                  <SelectValue placeholder="Profile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="incomplete">Incomplete</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <AntDTable
        showSettings
        data={filteredUsers}
        loading={isLoading}
        columns={columns}
        rowKey="id"
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows as UserDetails[]);
          },
          onSelectAll: selected => void handleSelectAll(selected),
          preserveSelectedRowKeys: true,
        }}
        pagination={{
          pageSize: pageSize,
          current: page,
          total: data?.total_users ?? 0,
          onChange: (page, size) => {
            setPage(page);
            setPageSize(size);
          },
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} users`,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        scroll={{ x: 1200 }}
        emptyText="No users found"
        className="ant-table-custom text-nunito"
      />
    </div>
  );
};

export default UserManagement;
